<template>
  <div class="project-detail-page">
    <div class="page-header">
      <a-space>
        <a-button @click="goBack">{{ t('common.back') }}</a-button>
        <h2 class="page-title">{{ headerTitle }}</h2>
      </a-space>
    </div>

    <a-spin :spinning="loading || (!project && !pageError)">
      <a-result
        v-if="!loading && pageError"
        status="error"
        :title="errorTitle"
      >
        <template #extra>
          <a-space>
            <a-button @click="goBack">{{ t('common.back') }}</a-button>
            <a-button type="primary" @click="load">{{ t('common.retry') }}</a-button>
          </a-space>
        </template>
      </a-result>

      <template v-else-if="project">
        <a-card :bordered="false" class="summary-card">
          <a-descriptions :column="2" size="small">
            <a-descriptions-item :label="tv('creator')">
              {{ formatTaskUserLabel(project.creator, project.creator_id) }}
            </a-descriptions-item>
            <a-descriptions-item :label="tv('createdAt')">
              {{ formatDate(project.created_at) }}
            </a-descriptions-item>
            <a-descriptions-item :label="tv('aggregateLabel')" :span="2">
              {{ aggregateText }}
            </a-descriptions-item>
          </a-descriptions>
          <a-space v-if="access.can_add_work_item" class="add-actions">
            <a-button type="primary" @click="goUploadVideo">
              {{ tv('addVideoItem') }}
            </a-button>
            <a-button @click="goUploadDocument">
              {{ tv('addDocumentItem') }}
            </a-button>
            <a-button @click="goLessonDesign">
              {{ tv('addLessonItem') }}
            </a-button>
          </a-space>
        </a-card>

        <a-card v-if="!loading && !hasVisibleSections" :bordered="false">
          <a-empty :description="tv('emptyItems')">
            <div class="empty-desc">{{ tv('emptyItemsDesc') }}</div>
          </a-empty>
        </a-card>

        <a-card
          v-if="lessonItems.length > 0"
          :bordered="false"
          class="section-card"
          :title="tv('sectionLesson')"
        >
          <WorkItemSectionTable :items="lessonItems" />
        </a-card>

        <a-card
          v-if="videoItems.length > 0"
          :bordered="false"
          class="section-card"
          :title="tv('sectionVideo')"
        >
          <WorkItemSectionTable :items="videoItems" />
        </a-card>

        <a-card
          v-if="documentItems.length > 0"
          :bordered="false"
          class="section-card"
          :title="tv('sectionDocument')"
        >
          <WorkItemSectionTable :items="documentItems" />
        </a-card>
      </template>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { summarizeFolderStatusCounts } from '@/modules/project/api/work-item'
import WorkItemSectionTable from '@/modules/project/components/WorkItemSectionTable.vue'
import { useProjectDetailLoader } from '@/modules/project/composables/useProjectDetailLoader'
import { useProjectI18n } from '@/modules/project/i18n/use-project-i18n'
import { partitionWorkItemsByType } from '@/modules/project/utils/work-item-sections'
import { formatTaskUserLabel } from '@/modules/task/utils/task-display'
import { useUserStore } from '@/stores'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { tv } = useProjectI18n('detail')
const userStore = useUserStore()

const {
  loading,
  project,
  workItems,
  statusCounts,
  access,
  loadProjectDetail,
  reset,
} = useProjectDetailLoader()

const pageError = ref(false)
const notFound = ref(false)

const headerTitle = computed(() => project.value?.title || tv('pageTitle'))
const errorTitle = computed(() => (notFound.value ? tv('notFound') : tv('loadFailed')))

const aggregateText = computed(() => {
  const summary = summarizeFolderStatusCounts(statusCounts.value)
  return t('project.list.aggregateSummary', {
    total: summary.total,
    completed: summary.completed,
    submitted: summary.submitted,
    processing: summary.processing,
  })
})

const sections = computed(() => partitionWorkItemsByType(workItems.value))

const lessonItems = computed(() => sections.value.lesson)
const videoItems = computed(() => sections.value.video)
const documentItems = computed(() => sections.value.document)

const hasVisibleSections = computed(
  () =>
    lessonItems.value.length > 0 ||
    videoItems.value.length > 0 ||
    documentItems.value.length > 0,
)

function formatDate(value?: string | null): string {
  if (!value) return t('common.none')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function goBack() {
  router.push({ name: 'MyProjects' })
}

function goUploadVideo() {
  const id = project.value?.id
  router.push(id ? `/video/upload?project_id=${id}` : '/video/upload')
}

function goUploadDocument() {
  const id = project.value?.id
  router.push(id ? `/doc-workflow/upload?project_id=${id}` : '/doc-workflow/upload')
}

function goLessonDesign() {
  const id = project.value?.id
  router.push(id ? `/teaching/lesson/workspace?project_id=${id}` : '/teaching/lesson/workspace')
}

function parseProjectId(): number | null {
  const rawId = route.params.id
  const projectId = Number(Array.isArray(rawId) ? rawId[0] : rawId)
  if (!Number.isFinite(projectId) || projectId <= 0) return null
  return projectId
}

async function load() {
  pageError.value = false
  notFound.value = false
  const projectId = parseProjectId()
  if (projectId == null) {
    reset()
    notFound.value = true
    pageError.value = true
    return
  }

  try {
    const bundle = await loadProjectDetail(projectId, userStore.userInfo?.id ?? null)
    if (!bundle.project) {
      notFound.value = true
      pageError.value = true
    }
  } catch (error) {
    console.error('Failed to load project detail:', error)
    pageError.value = true
  }
}

watch(
  () => route.params.id,
  () => {
    void load()
  },
  { immediate: true },
)

onUnmounted(() => {
  reset()
})
</script>

<style lang="less" scoped>
.project-detail-page {
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 16px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.summary-card,
.section-card {
  margin-bottom: 16px;
}

.add-actions {
  margin-top: 16px;
}

.empty-desc {
  margin-top: 8px;
  color: #8c8c8c;
}
</style>
